import { useState } from 'react';
import { GlassWater, Ticket } from 'lucide-react';
import { post } from './api';
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Skeleton, useToast } from './ui';
import { attempt, useLoad } from './useLoad';

interface OpenBottle { id: string; product: string; store: string; openedAt: string; capacityMl: string; soldMl: string; leftMl: string }
interface BottleRow { productId: string; name: string; opened: number; emptied: number; soldMl: string; lostMl: string; lossPct: string }

const ml = (v: string) => `${Number(v).toLocaleString('pt-PT', { maximumFractionDigits: 1 })} ml`;
const when = (iso: string) => new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/**
 * Doses (docs/14 Fase 2): o que resta em cada garrafa aberta e as perdas do
 * período — o que faltava vender quando a garrafa acabou (doses mais cheias,
 * ofertas, partidas). Acima de 10% vale a pena olhar.
 */
export function BarPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [p, setP] = useState({ from: `${today.slice(0, 8)}01`, to: today });
  const { data, error, reload } = useLoad<{ open: OpenBottle[]; products: BottleRow[] }>(`/bar/bottles?from=${p.from}&to=${p.to}`);
  const toast = useToast();
  const [err, setErr] = useState<string | null>(null);
  const empty = async (b: OpenBottle) => {
    const e = await attempt(() => post(`/bar/bottles/${b.id}/empty`, {}));
    setErr(e);
    if (!e) { toast(`Garrafa de ${b.product} fechada`); await reload(); }
  };
  return (
    <div className="max-w-6xl space-y-6">
      {(error || err) && <Alert>{error ?? err}</Alert>}
      <Card title="Garrafas abertas" description="Quanto resta, pelas doses vendidas. Quando uma acaba, feche-a aqui ou na caixa (Garrafa vazia)." padded={false}>
        {!data ? <div className="p-4"><Skeleton rows={3} /></div> : !data.open.length ? (
          <EmptyState icon={GlassWater} title="Nenhuma garrafa aberta" text="A garrafa abre-se sozinha com a primeira dose vendida." />
        ) : (
          <ul className="divide-y divide-[var(--border)]">
            {data.open.map((b) => {
              const left = Math.max(0, Math.min(100, (Number(b.leftMl) / Number(b.capacityMl)) * 100));
              return (
                <li key={b.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                  <div className="min-w-48 flex-1">
                    <div className="font-medium">{b.product}</div>
                    <div className="text-xs text-muted">{b.store} · aberta {when(b.openedAt)}</div>
                  </div>
                  <div className="w-48">
                    <div className="h-2 overflow-hidden rounded-full bg-surface-2"><div className="h-full rounded-full bg-brand-700" style={{ width: `${left}%` }} /></div>
                    <div className="num mt-1 text-xs text-muted">restam {ml(b.leftMl)} de {ml(b.capacityMl)}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => void empty(b)}>Garrafa vazia</Button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="De"><Input type="date" className="w-45" value={p.from} onChange={(e) => setP({ ...p, from: e.target.value })} /></Field>
          <Field label="Até"><Input type="date" className="w-45" value={p.to} onChange={(e) => setP({ ...p, to: e.target.value })} /></Field>
        </div>
      </Card>
      {data && (
        <Card title="Doses e perdas" description="Garrafas abertas no período: o que se vendeu à dose e o que faltava quando acabaram." padded={false}>
          {!data.products.length ? <EmptyState icon={GlassWater} title="Sem doses vendidas neste período" text='Na ficha do artigo (secção Bar), marque a garrafa com a capacidade e crie a dose que sai dela.' /> : (
            <div className="overflow-x-auto p-2 xl:p-3">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted">
                  <tr><th className="px-2 py-2">Garrafa</th><th className="px-2 text-right">Abertas</th><th className="hidden px-2 text-right md:table-cell">Vendido</th><th className="px-2 text-right">Perda</th><th className="px-2 text-right">%</th></tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {data.products.map((r) => (
                    <tr key={r.productId}>
                      <td className="px-2 py-2.5 font-medium">{r.name}</td>
                      <td className="num px-2 text-right">{r.opened}<div className="text-xs text-muted">{r.emptied} acabaram</div></td>
                      <td className="num hidden px-2 text-right md:table-cell">{ml(r.soldMl)}</td>
                      <td className="num px-2 text-right">{ml(r.lostMl)}</td>
                      <td className="num px-2 text-right"><Badge tone={Number(r.lossPct) > 10 ? 'brand' : Number(r.lossPct) > 5 ? 'warn' : 'ok'}>{r.lossPct.replace('.', ',')}%</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

interface NightRow { night: string; sale: number; ticket: number; guest: number; total: number }
interface GuestRow { night: string; at: string; people: number; note: string | null; operator: string | null }
interface TicketRow { name: string; eventDate: string | null; sold: number; used: number; voided: number; open: number }
const dmy = (d: string) => d.slice(0, 10).split('-').reverse().join('/');
const weekday = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('pt-PT', { weekday: 'short', timeZone: 'UTC' });

/**
 * Entradas e bilhetes (docs/14 Fase 2): pessoas por noite (à porta, com
 * bilhete, convidados), quem deixou entrar convidados e os bilhetes vendidos
 * no período — usados, por usar e anulados. A noite vai até às 06:00.
 */
export function DoorPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [p, setP] = useState({ from: `${today.slice(0, 8)}01`, to: today });
  const { data, error } = useLoad<{ nights: NightRow[]; guests: GuestRow[]; tickets: TicketRow[] }>(`/door/nights?from=${p.from}&to=${p.to}`);
  const sum = (k: keyof Omit<NightRow, 'night'>) => (data?.nights ?? []).reduce((s, n) => s + n[k], 0);
  return (
    <div className="max-w-6xl space-y-6">
      {error && <Alert>{error}</Alert>}
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="De"><Input type="date" className="w-45" value={p.from} onChange={(e) => setP({ ...p, from: e.target.value })} /></Field>
          <Field label="Até"><Input type="date" className="w-45" value={p.to} onChange={(e) => setP({ ...p, to: e.target.value })} /></Field>
        </div>
      </Card>
      {!data ? <Card><Skeleton rows={4} /></Card> : <>
        <Card title="Pessoas por noite" description="À porta (entradas vendidas), com bilhete (lidos à porta) e convidados. As devoluções já estão descontadas." padded={false}>
          {!data.nights.length ? <EmptyState icon={Ticket} title="Ninguém entrou neste período" text='Crie as entradas no catálogo (secção Entrada da ficha) e use Caixa → Porta para os bilhetes e os convidados.' /> : (
            <div className="overflow-x-auto p-2 xl:p-3">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted">
                  <tr><th className="px-2 py-2">Noite</th><th className="px-2 text-right">À porta</th><th className="px-2 text-right">Bilhete</th><th className="px-2 text-right">Convidados</th><th className="px-2 text-right">Total</th></tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {data.nights.map((n) => (
                    <tr key={n.night}>
                      <td className="px-2 py-2.5 font-medium">{dmy(n.night)} <span className="text-xs text-muted">{weekday(n.night)}</span></td>
                      <td className="num px-2 text-right">{n.sale}</td>
                      <td className="num px-2 text-right">{n.ticket}</td>
                      <td className="num px-2 text-right">{n.guest}</td>
                      <td className="num px-2 text-right font-semibold">{n.total}</td>
                    </tr>
                  ))}
                </tbody>
                {data.nights.length > 1 && <tfoot className="border-t-2 border-[var(--border)] font-semibold">
                  <tr><td className="px-2 py-2.5">Período</td><td className="num px-2 text-right">{sum('sale')}</td><td className="num px-2 text-right">{sum('ticket')}</td><td className="num px-2 text-right">{sum('guest')}</td><td className="num px-2 text-right">{sum('total')}</td></tr>
                </tfoot>}
              </table>
            </div>
          )}
        </Card>

        <Card title="Bilhetes vendidos" description="Vendidos no período (venda antecipada): os por usar ainda podem entrar; os anulados foram devolvidos." padded={false}>
          {!data.tickets.length ? <EmptyState icon={Ticket} title="Nenhum bilhete vendido neste período" text='Um artigo com "Bilhete com QR" imprime um bilhete por unidade, com o código que a porta lê.' /> : (
            <div className="overflow-x-auto p-2 xl:p-3">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted">
                  <tr><th className="px-2 py-2">Bilhete</th><th className="px-2">Noite</th><th className="px-2 text-right">Vendidos</th><th className="px-2 text-right">Entraram</th><th className="px-2 text-right">Por usar</th><th className="hidden px-2 text-right sm:table-cell">Anulados</th></tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {data.tickets.map((t) => (
                    <tr key={`${t.name}-${t.eventDate}`}>
                      <td className="px-2 py-2.5 font-medium">{t.name}</td>
                      <td className="px-2">{t.eventDate ? dmy(t.eventDate) : <span className="text-muted">qualquer</span>}</td>
                      <td className="num px-2 text-right">{t.sold}</td>
                      <td className="num px-2 text-right">{t.used}</td>
                      <td className="num px-2 text-right">{t.open > 0 ? <Badge tone="warn">{t.open}</Badge> : 0}</td>
                      <td className="num hidden px-2 text-right sm:table-cell">{t.voided}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Convidados" description="Quem entrou sem pagar, de quem eram e quem os deixou entrar." padded={false}>
          {!data.guests.length ? <p className="p-4 text-sm text-muted">Sem convidados neste período.</p> : (
            <ul className="divide-y divide-[var(--border)]">
              {data.guests.map((g, i) => (
                <li key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
                  <span className="num w-28 text-muted">{when(g.at)}</span>
                  <span className="flex-1 font-medium">{g.note}</span>
                  <span className="num">{g.people === 1 ? '1 pessoa' : `${g.people} pessoas`}</span>
                  <span className="w-32 truncate text-muted">{g.operator ?? '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </>}
    </div>
  );
}
